import { getTeamForPlayer } from './teamUtils.js';

/**
 * RondaLogic class handles the core game logic for Ronda.
 * 
 * Features:
 * - Validates moves
 * - Processes card throws (matching and collecting)
 * - Manages turn progression
 * - Handles "Caída", "Contra", "San Vicente"
 * - Manages "Ronda" and "Rondín" declarations and envíos
 */
class RondaLogic {
    constructor() {
        // No specific state needed here as it will be passed in methods
        // or we can store game state if we want to make it stateful
    }

    /**
     * Detect if a player has a Ronda (2 cards of the same value) or Rondín (3 cards of the same value)
     * @param {Array} cards - The player's hand (3 cards)
     * @returns {Object|null} { type: 'ronda'|'rondin', value: number, cards: [] } or null
     */
    detectRondaOrRondin(cards) {
        if (!cards || cards.length < 2) return null;

        // Count cards by value
        const valueCounts = {};
        cards.forEach(card => {
            valueCounts[card.value] = (valueCounts[card.value] || 0) + 1;
        });

        // Check for Rondín (3 of a kind) first
        for (const [value, count] of Object.entries(valueCounts)) {
            if (count === 3) {
                return {
                    type: 'rondin',
                    value: parseInt(value),
                    cards: cards.filter(c => c.value === parseInt(value)),
                    points: 5 // Rondín vale 5 puntos
                };
            }
        }

        // Check for Ronda (2 of a kind)
        for (const [value, count] of Object.entries(valueCounts)) {
            if (count === 2) {
                return {
                    type: 'ronda',
                    value: parseInt(value),
                    cards: cards.filter(c => c.value === parseInt(value)),
                    points: 2 // Ronda vale 2 puntos
                };
            }
        }

        return null;
    }

    /**
     * Detect all Rondas and Rondines for all players
     * @param {Object} playerCards - { 0: [], 1: [], 2: [], 3: [] }
     * @returns {Array} Array of { playerIndex, type, value, cards, points }
     */
    detectAllRondasAndRondines(playerCards) {
        const declarations = [];

        for (let playerIndex = 0; playerIndex < 4; playerIndex++) {
            const cards = playerCards[playerIndex];
            const declaration = this.detectRondaOrRondin(cards);

            if (declaration) {
                declarations.push({
                    playerIndex,
                    ...declaration
                });
            }
        }

        return declarations;
    }

    /**
     * Process the declarations according to the rules:
     * - If there are Rondines and Rondas from opposing teams, Rondines cancel opposing Rondas
     * - Teammates' declarations are cumulative
     * @param {Array} declarations - Array of all declarations
     * @returns {Object} { validDeclarations: [], needsEnvio: boolean, envioParticipants: [] }
     */
    processDeclarations(declarations) {
        if (declarations.length === 0) {
            return { validDeclarations: [], needsEnvio: false, envioParticipants: [] };
        }

        // Separate by teams (Team 1: players 0, 2; Team 2: players 1, 3)
        const team1Declarations = declarations.filter(d => getTeamForPlayer(d.playerIndex) === 1);
        const team2Declarations = declarations.filter(d => getTeamForPlayer(d.playerIndex) === 2);

        // Check if any team has Rondines
        const team1HasRondin = team1Declarations.some(d => d.type === 'rondin');
        const team2HasRondin = team2Declarations.some(d => d.type === 'rondin');

        let validDeclarations = [...declarations];

        // Rondines cancel opposing team's Rondas (not Rondines)
        if (team1HasRondin) {
            // Cancel team 2's Rondas (but not Rondines)
            validDeclarations = validDeclarations.filter(d => {
                if (getTeamForPlayer(d.playerIndex) === 2 && d.type === 'ronda') {
                    return false;
                }
                return true;
            });
        }

        if (team2HasRondin) {
            // Cancel team 1's Rondas (but not Rondines)
            validDeclarations = validDeclarations.filter(d => {
                if (getTeamForPlayer(d.playerIndex) === 1 && d.type === 'ronda') {
                    return false;
                }
                return true;
            });
        }

        // Check if envío is needed (2+ declarations from different teams)
        const validTeam1 = validDeclarations.filter(d => getTeamForPlayer(d.playerIndex) === 1);
        const validTeam2 = validDeclarations.filter(d => getTeamForPlayer(d.playerIndex) === 2);

        const needsEnvio = validTeam1.length > 0 && validTeam2.length > 0;

        // Get players who participate in envío (those with valid declarations from opposing teams)
        const envioParticipants = needsEnvio ? validDeclarations.map(d => d.playerIndex) : [];

        return {
            validDeclarations,
            needsEnvio,
            envioParticipants
        };
    }

    /**
     * Resolve an envío
     * @param {Array} declarations - Valid declarations in the envío
     * @param {string} resolution - 'accepted', 'declined', 'a_la_mayor'
     * @param {number} winnerPlayerIndex - The player who wins (has the higher value, or mano if tied)
     * @param {number} manoPlayerIndex - The player who goes first (mano)
     * @returns {Object} { winningTeam, totalPoints, declarations }
     */
    resolveEnvio(declarations, resolution, winnerPlayerIndex, manoPlayerIndex) {
        if (resolution === 'declined') {
            // The player who sent (envió) gets only their own points (and teammate's if applicable)
            // This is handled by the caller knowing who sent
            return {
                resolution: 'declined',
                winningTeam: null,
                totalPoints: 0,
                declarations: []
            };
        }

        // Find the winning declaration
        let winningDeclaration = null;
        let maxValue = -1;

        for (const decl of declarations) {
            // Compare by type first (rondin > ronda), then by value
            const typeWeight = decl.type === 'rondin' ? 1000 : 0;
            const effectiveValue = typeWeight + decl.value;

            if (effectiveValue > maxValue) {
                maxValue = effectiveValue;
                winningDeclaration = decl;
            } else if (effectiveValue === maxValue) {
                // Tie: winner is whoever is closest to mano (in turn order)
                // Turn order after mano is: mano, mano+1, mano+2, mano+3
                const currentWinnerDistance = (winningDeclaration.playerIndex - manoPlayerIndex + 4) % 4;
                const newDistance = (decl.playerIndex - manoPlayerIndex + 4) % 4;
                if (newDistance < currentWinnerDistance) {
                    winningDeclaration = decl;
                }
            }
        }

        const winningTeam = getTeamForPlayer(winningDeclaration.playerIndex);

        if (resolution === 'a_la_mayor') {
            // Only count points for the winning team's declarations (normal points, not accumulated)
            const teamDeclarations = declarations.filter(d => getTeamForPlayer(d.playerIndex) === winningTeam);

            const totalPoints = teamDeclarations.reduce((sum, d) => sum + d.points, 0);

            return {
                resolution: 'a_la_mayor',
                winningTeam,
                winningDeclaration,
                totalPoints,
                declarations: teamDeclarations
            };
        }

        if (resolution === 'accepted') {
            // Winner gets all the points from all declarations
            const totalPoints = declarations.reduce((sum, d) => sum + d.points, 0);

            return {
                resolution: 'accepted',
                winningTeam,
                winningDeclaration,
                totalPoints,
                declarations
            };
        }

        return null;
    }

    /**
     * Determine whether playing `cardValue` reveals a "media" (a card that
     * completes part of the player's declared Ronda/Rondín) or an "además"
     * (a card played that is NOT part of the declaration), so the UI knows
     * what to announce. Returns null when nothing should be announced
     * (no declaration for this player, or it hasn't been officially
     * announced yet during the envío phase).
     *
     * @param {number} playerIndex
     * @param {number} cardValue - Value of the card being played
     * @param {Object} rondaState - { validDeclarations, envioHistory, resolution, decliningTeam, revealedMedias }
     * @returns {{kind: 'media', shouldShow: boolean, revealedCount: number, isLastMedia: boolean}
     *          | {kind: 'ademas', shouldShow: boolean} | null}
     */
    checkMediaOrAdemas(playerIndex, cardValue, rondaState) {
        const playerDeclaration = rondaState.validDeclarations?.find(d => d.playerIndex === playerIndex);
        if (!playerDeclaration) return null;

        const playerAnnounced = !rondaState.envioHistory?.length ||
            rondaState.envioHistory.some(h => h.playerIndex === playerIndex && h.action === 'cantar') ||
            rondaState.resolution === 'a_la_mayor';
        if (!playerAnnounced) return null;

        const playerTeam = getTeamForPlayer(playerIndex);
        const shouldShow = rondaState.resolution !== 'declined' ||
            (rondaState.resolution === 'declined' && playerTeam !== rondaState.decliningTeam);

        if (playerDeclaration.value === cardValue) {
            const revealedCount = (rondaState.revealedMedias?.[playerIndex] || 0) + 1;
            const isLastMedia = revealedCount >= (playerDeclaration.type === 'rondin' ? 3 : 2);
            return { kind: 'media', shouldShow, revealedCount, isLastMedia };
        }

        return { kind: 'ademas', shouldShow };
    }

    /**
     * Given the medias revealed so far for an 'accepted'/'a_la_mayor' envío
     * (RONDA_ONLINE.md Fase 11), determine whether enough information exists
     * to declare a winner yet, and if so, what they win. Pure extraction of
     * the "canDetermineWinner" logic from Ronda.jsx's
     * checkAndResolveEnvioAfterMedias — same rules (need the best
     * declaration from each team revealed, or the sole declaration of a
     * team with only one, with an early-exit special case for a revealed
     * Rey/12), minus its UI/marker/toast side effects. Used by the backend
     * (RondaTable.playCard) so a media revealed mid-hand can resolve a
     * pending envío at the exact moment the offline client would.
     * @param {Array} validDeclarations
     * @param {Object} revealedMedias - { [playerIndex]: revealedCount }
     * @param {number} manoPlayerIndex
     * @param {'accepted'|'a_la_mayor'} resolutionType
     * @returns {{resolved: false} | {resolved: true, winningTeam: 1|2, winningDeclaration: Object, totalPoints: number}}
     */
    checkEnvioResolution(validDeclarations, revealedMedias, manoPlayerIndex, resolutionType) {
        const team1Declarations = validDeclarations.filter(d => getTeamForPlayer(d.playerIndex) === 1);
        const team2Declarations = validDeclarations.filter(d => getTeamForPlayer(d.playerIndex) === 2);

        const getBestDeclaration = (declarations) => {
            return declarations.reduce((best, decl) => {
                const typeWeight = decl.type === 'rondin' ? 1000 : 0;
                const effectiveValue = typeWeight + decl.value;
                const bestTypeWeight = best ? (best.type === 'rondin' ? 1000 : 0) : -1;
                const bestEffectiveValue = best ? bestTypeWeight + best.value : -1;
                return effectiveValue > bestEffectiveValue ? decl : best;
            }, null);
        };

        const team1Best = getBestDeclaration(team1Declarations);
        const team2Best = getBestDeclaration(team2Declarations);

        const team1Revealed = team1Declarations.filter(d => (revealedMedias[d.playerIndex] || 0) > 0);
        const team2Revealed = team2Declarations.filter(d => (revealedMedias[d.playerIndex] || 0) > 0);

        const team1BestRevealed = team1Best && (revealedMedias[team1Best.playerIndex] || 0) > 0;
        const team2BestRevealed = team2Best && (revealedMedias[team2Best.playerIndex] || 0) > 0;

        const team1OnlyOneDeclAndRevealed = team1Declarations.length === 1 && team1Revealed.length === 1;
        const team2OnlyOneDeclAndRevealed = team2Declarations.length === 1 && team2Revealed.length === 1;

        let canDetermineWinner = (team1BestRevealed && team2BestRevealed) ||
            (team1OnlyOneDeclAndRevealed && team2BestRevealed) ||
            (team2OnlyOneDeclAndRevealed && team1BestRevealed);

        let winningDeclaration = null;

        // Special case: a revealed Rey (12) already wins if it's closer to
        // mano than anything the other team could still reveal.
        if (team1Best && team1Best.value === 12 && team1BestRevealed) {
            const team1BestDistance = (team1Best.playerIndex - manoPlayerIndex + 4) % 4;
            const team2BestDistance = team2Best ? (team2Best.playerIndex - manoPlayerIndex + 4) % 4 : 999;
            if (team1BestDistance < team2BestDistance) {
                canDetermineWinner = true;
                winningDeclaration = team1Best;
            }
        }
        if (team2Best && team2Best.value === 12 && team2BestRevealed) {
            const team2BestDistance = (team2Best.playerIndex - manoPlayerIndex + 4) % 4;
            const team1BestDistance = team1Best ? (team1Best.playerIndex - manoPlayerIndex + 4) % 4 : 999;
            if (team2BestDistance < team1BestDistance) {
                canDetermineWinner = true;
                winningDeclaration = team2Best;
            }
        }

        if (canDetermineWinner && team1Best && team2Best) {
            const team1EffectiveValue = (team1Best.type === 'rondin' ? 1000 : 0) + team1Best.value;
            const team2EffectiveValue = (team2Best.type === 'rondin' ? 1000 : 0) + team2Best.value;

            if (team1EffectiveValue > team2EffectiveValue) {
                winningDeclaration = team1Best;
            } else if (team2EffectiveValue > team1EffectiveValue) {
                winningDeclaration = team2Best;
            } else {
                const team1Distance = (team1Best.playerIndex - manoPlayerIndex + 4) % 4;
                const team2Distance = (team2Best.playerIndex - manoPlayerIndex + 4) % 4;
                winningDeclaration = team1Distance < team2Distance ? team1Best : team2Best;
            }
        }

        if (!canDetermineWinner || !winningDeclaration) {
            return { resolved: false };
        }

        const winningTeam = getTeamForPlayer(winningDeclaration.playerIndex);

        let totalPoints;
        if (resolutionType === 'a_la_mayor') {
            const winningTeamDeclarations = validDeclarations.filter(d => getTeamForPlayer(d.playerIndex) === winningTeam);
            totalPoints = winningTeamDeclarations.reduce((sum, d) => sum + d.points, 0);
        } else {
            totalPoints = validDeclarations.reduce((sum, d) => sum + d.points, 0);
        }

        return { resolved: true, winningTeam, winningDeclaration, totalPoints };
    }

    /**
     * Resolve the type/points of a Caída chain. Single source of truth for
     * these values — previously duplicated (with diverging point totals) in
     * two different places in the Ronda component.
     * @param {number} currentCaidaCount - 0/1 (no caída), 2 (Caída), 3 (Contracaída), 4 (San Vicente)
     * @returns {{caidaType: string|null, caidaPoints: number}}
     */
    getCaidaResult(currentCaidaCount) {
        switch (currentCaidaCount) {
            case 2: return { caidaType: 'Caída', caidaPoints: 1 };
            case 3: return { caidaType: 'Contracaída', caidaPoints: 3 };
            case 4: return { caidaType: 'San Vicente', caidaPoints: 4 };
            default: return { caidaType: null, caidaPoints: 0 };
        }
    }

    /**
     * Determine whether an Aseo (clearing the center) awards points.
     * Rule: an Aseo is invalid ONLY when the dealer plays their last card in
     * the last hand of the round and every other player has already emptied
     * their hand (i.e. it's the very last card of the round).
     * @param {Object} params
     * @param {boolean} params.isLastHand - No cards remain in the deck
     * @param {boolean} params.isDealerLastToPlay - The collector is the dealer
     * @param {boolean} params.allOtherPlayersEmpty - Every other player has 0 cards
     * @param {boolean} params.playerCardsAfterPlay - Cards left in the collector's hand after this play
     * @returns {boolean}
     */
    isAseoValid({ isLastHand, isDealerLastToPlay, allOtherPlayersEmpty, playerCardsAfterPlay }) {
        return !(isLastHand && isDealerLastToPlay && allOtherPlayersEmpty && playerCardsAfterPlay === 0);
    }

    /**
     * Get card value name for display
     * @param {number} value
     * @returns {string}
     */
    getCardValueName(value) {
        const names = {
            1: 'As',
            2: 'Dos',
            3: 'Tres',
            4: 'Cuatro',
            5: 'Cinco',
            6: 'Seis',
            7: 'Siete',
            10: 'Sota',
            11: 'Caballo',
            12: 'Rey'
        };
        return names[value] || value.toString();
    }

    /**
     * Process a player's move (throwing a card)
     * @param {Object} playedCard - The card played by the player
     * @param {Array} centerCards - Current cards in the center
     * @returns {Object} Result of the move
     */
    processMove(playedCard, centerCards) {
        // Sort center cards by value for easier checking
        // We need to keep track of original indices if we were modifying the array directly,
        // but here we will return which cards to collect.

        const matches = [];
        const collectedCards = [];

        // 1. Check for direct match
        const directMatchIndex = centerCards.findIndex(c => c.value === playedCard.value);

        if (directMatchIndex !== -1) {
            // Found a match!
            // Found a match!
            // First, find ALL cards in the center that match the value (needed for San Vicente/Contracaída cleanup)
            const allMatchingValIndices = centerCards
                .map((c, i) => c.value === playedCard.value ? i : -1)
                .filter(i => i !== -1);

            allMatchingValIndices.forEach(idx => {
                const card = centerCards[idx];
                if (!matches.includes(card)) {
                    matches.push(card);
                    collectedCards.push(card);
                }
            });
            collectedCards.push(playedCard); // The played card is also collected

            // 2. Check for consecutive higher values (The Ladder)
            // We need to look for value + 1, value + 2, etc. in the remaining center cards
            let nextValue = playedCard.value + 1;

            // Handle special cases if any (e.g. 7 -> 10 in some variants? No, usually 1-7, 10-12)
            // In Spanish deck: 1, 2, 3, 4, 5, 6, 7, 10, 11, 12.
            // Some regions play 7 -> 10 as consecutive.
            // Let's assume standard numerical continuity for now, but check deck type.
            // If using standard 40 card deck: 7 is followed by 10? 
            // Usually in Ronda: 1,2,3,4,5,6,7,10,11,12.
            // If I match a 7, do I pick up a 10? 
            // The rules say "consecutivamente del valor superior".
            // In many Spanish card games, 7 and 10 are NOT consecutive numerically, but are in the deck order.
            // Let's implement deck order consecutiveness.

            const deckOrder = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12];
            let currentOrderIndex = deckOrder.indexOf(playedCard.value);

            if (currentOrderIndex !== -1) {
                // Look for next cards in the sequence
                while (currentOrderIndex < deckOrder.length - 1) {
                    currentOrderIndex++;
                    const targetValue = deckOrder[currentOrderIndex];

                    // Check if this value exists in center cards (excluding ones already matched)
                    // Note: There might be multiple cards of same value in center? 
                    // Rules say: "Si alguna de las 4 cartas centrales se repite el jugador que reparte echará otra... hasta que las 4 sean diferentes."
                    // So initially unique. But during gameplay?
                    // "Si no tienes cartas para coger echarás una de tus tres cartas a la mesa".
                    // So yes, there can be duplicates in the center later on.
                    // If there are duplicates, which one do we pick? Usually doesn't matter, just one.

                    const consecutiveMatchIndex = centerCards.findIndex(c =>
                        c.value === targetValue && !matches.includes(c)
                    );

                    if (consecutiveMatchIndex !== -1) {
                        const consecutiveCard = centerCards[consecutiveMatchIndex];
                        matches.push(consecutiveCard);
                        collectedCards.push(consecutiveCard);
                    } else {
                        // Sequence broken
                        break;
                    }
                }
            }

            return {
                action: 'COLLECT',
                playedCard: playedCard,
                collectedCards: collectedCards, // Includes playedCard + matched cards
                centerCardsToRemove: matches // Only the cards from center to remove
            };
        } else {
            // No match, card goes to center
            return {
                action: 'DROP',
                playedCard: playedCard
            };
        }
    }

    /**
     * Get the next player index
     * @param {number} currentTurnIndex 
     * @returns {number}
     */
    getNextTurn(currentTurnIndex) {
        return (currentTurnIndex + 1) % 4;
    }
}

export default RondaLogic;
